import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Supported Living', path: '/supported-living' },
    { name: 'About Us', path: '/about' }
  ];

  const serviceLinks = [
    { name: 'Staffing Solutions', path: '/staffing' },
    { name: 'Recruitment', path: '/recruitment' },
    { name: 'Compliance', path: '/compliance' },
    { name: 'Contact Us', path: '/contact' }
  ];

  return (
    <footer className="bg-gradient-to-br from-[#1A2332] to-[#2C3E50] text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <img 
              src="/logo.jpg" 
              alt="Three Spheres Healthcare" 
              className="h-16 w-auto mb-4 bg-white rounded-lg p-2"
            />
            <p className="text-gray-300 text-sm mb-4">
              RMN-led mental health and community care services across the United Kingdom.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                className="text-gray-300 hover:text-[#4A90E2] transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="#" 
                className="text-gray-300 hover:text-[#4A90E2] transition-colors"
                aria-label="Twitter"
              >
                <Twitter size={20} />
              </a>
              <a 
                href="#" 
                className="text-gray-300 hover:text-[#4A90E2] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-[#4A90E2] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-[#4A90E2] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Compliance & Policies */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Compliance</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/compliance/privacy-notice"
                  className="text-gray-300 hover:text-[#4A90E2] transition-colors text-sm"
                >
                  Privacy Notice
                </Link>
              </li>
              <li>
                <Link 
                  to="/cookie-policy"
                  className="text-gray-300 hover:text-[#4A90E2] transition-colors text-sm"
                >
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/compliance/complaints"
                  className="text-gray-300 hover:text-[#4A90E2] transition-colors text-sm"
                >
                  Complaints
                </Link>
              </li>
              <li>
                <Link 
                  to="/compliance/safeguarding-adults"
                  className="text-gray-300 hover:text-[#4A90E2] transition-colors text-sm"
                >
                  Safeguarding
                </Link>
              </li>
              <li>
                <Link 
                  to="/compliance/cqc-status"
                  className="text-gray-300 hover:text-[#4A90E2] transition-colors text-sm"
                >
                  CQC Status
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone size={18} className="mt-1 text-[#4A90E2]" />
                <div>
                  <a 
                    href="tel:03300433693" 
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    0330 043 3693
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="mt-1 text-[#4A90E2]" />
                <div>
                  <a 
                    href="mailto:info@threesphereshealthcare.org" 
                    className="text-gray-300 hover:text-white transition-colors text-sm break-all"
                  >
                    info@threesphereshealthcare.org
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-1 text-[#4A90E2]" />
                <div className="text-gray-300 text-sm">
                  United Kingdom
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Three Spheres Healthcare Limited. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link 
                to="/compliance/privacy-notice" 
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Privacy Notice
              </Link>
              <Link 
                to="/cookie-policy" 
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Cookie Policy
              </Link>
              <Link 
                to="/compliance/complaints" 
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Complaints
              </Link>
              <Link 
                to="/compliance/safeguarding-adults" 
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Safeguarding
              </Link>
              <Link 
                to="/compliance/cqc-status" 
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                CQC Status
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
